// Redux Utils
import { useDispatch, useSelector } from "react-redux";
import { registerUser } from "@/features/register/registerThunks";
import { registerReset } from "@/features/register/registerSlice";

// React Utils
import { useState, useRef } from "react";
import { Navigate, useNavigate, useLocation } from "react-router-dom";
// assets
import SignInImage from "@/assets/FormSignIn.webp";
import BgImage from "@/assets/background.webp";
import AppLayout from "@/assets/appwhoop_app_layout.webp";
import LogoAppWhoop from "@/assets/appwhoop_ticket_logo.webp";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { motion as Motion, AnimatePresence } from "framer-motion";
// components
import Tooltip from "@/components/Tooltip";
// utils
import { validarRUT, formatearRUT, validarCoherenciaRUTFecha } from "@/utils/rutValidator";

const GENDERS = [
  { label: "Soy Mujer", value: "mujer" },
  { label: "Soy Hombre", value: "hombre" },
  { label: "Otros", value: "otro" },
];

const MONTHS = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];

const SOCIAL_NETWORKS = [
  { label: "Instagram", value: "instagram" },
  { label: "Facebook", value: "facebook" },
  { label: "TikTok", value: "tiktok" },
];

export default function Register() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from;
  const { isAuthenticated } = useSelector((state) => state.auth);
  const { sendRegister, loading, error } = useSelector((state) => state.register);

  const [form, setForm] = useState({
    gender: "mujer",
    name: "",
    lastname: "",
    tipoDocumento: "rut",
    rut: "",
    numeroPasaporte: "",
    socialNetwork: "instagram",
    socialHandle: "",
    day: "",
    month: "Enero",
    year: "",
    email: "",
    password: "",
    repeatPassword: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const passwordInputRef = useRef(null);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const confirmPasswordInputRef = useRef(null);
  const [errors, setErrors] = useState({});
  const [rutError, setRutError] = useState("");

  const handleRutChange = (e) => {
    const raw = e.target.value.replace(/[^0-9kK]/g, "").toUpperCase();
    const formatted = raw.length > 1 ? formatearRUT(raw) : raw;
    setForm((prev) => ({ ...prev, rut: formatted }));
    if (raw.length >= 8) {
      setRutError(validarRUT(formatted) ? "" : "RUT inválido");
    } else {
      setRutError("");
    }
  };

  const handlePasaporteChange = (e) => {
    const val = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "");
    setForm((prev) => ({ ...prev, numeroPasaporte: val }));
  };

  const validate = () => {
    const newErrors = {};
    if (!form.name) newErrors.name = "Campo requerido";
    if (!form.lastname) newErrors.lastname = "Campo requerido";

    if (form.tipoDocumento === "rut") {
      if (!form.rut) newErrors.rut = "Campo requerido";
      else if (!validarRUT(form.rut)) newErrors.rut = "RUT inválido";
    }

    if (form.tipoDocumento === "pasaporte") {
      if (!form.numeroPasaporte) newErrors.numeroPasaporte = "Campo requerido";
      else if (!/^[A-Z0-9]{6,20}$/i.test(form.numeroPasaporte)) newErrors.numeroPasaporte = "Número de pasaporte inválido (6-20 caracteres, letras y números)";
    }

    // Fecha de nacimiento requerida y coherente con RUT
    const currentYear = new Date().getFullYear();
    const yearNum = parseInt(form.year);
    if (!form.day || !form.year) {
      newErrors.fechaNacimiento = "La fecha de nacimiento es requerida";
    } else if (isNaN(yearNum) || yearNum < 1900 || yearNum > currentYear - 13) {
      newErrors.fechaNacimiento = "Debes tener al menos 13 años para registrarte";
    } else if (form.tipoDocumento === "rut" && form.rut && validarRUT(form.rut)) {
      const coherencia = validarCoherenciaRUTFecha(form.rut, yearNum);
      if (!coherencia.valido) newErrors.fechaNacimiento = coherencia.advertencia;
    }

    if (!form.email) newErrors.email = "Campo requerido";
    if (form.email && !/\S+@\S+\.\S+/.test(form.email)) newErrors.email = "El email no es válido";
    if (!form.password) newErrors.password = "Campo requerido";
    if (form.password && form.password.length < 8) newErrors.password = "La contraseña debe tener al menos 8 caracteres";
    if (form.password && !/[A-Z]/.test(form.password)) newErrors.password = "La contraseña debe tener al menos una letra mayúscula";
    if (form.password && !/[a-z]/.test(form.password)) newErrors.password = "La contraseña debe tener al menos una letra minúscula";
    if (form.password && !/[0-9]/.test(form.password)) newErrors.password = "La contraseña debe tener al menos un número";
    if (form.password && !/[!@#$%^&*(),.?":{}|<>]/.test(form.password)) newErrors.password = "La contraseña debe tener al menos un símbolo";
    if (!form.repeatPassword) newErrors.repeatPassword = "Campo requerido";
    if (form.password !== form.repeatPassword) newErrors.repeatPassword = "Las contraseñas no coinciden";
    return newErrors;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => {
      const next = { ...prev, [name]: type === "checkbox" ? checked : value };
      // Si cambia mes o año, ajustar el día si supera el máximo válido
      if (name === "month" || name === "year") {
        const monthIndex = MONTHS.indexOf(name === "month" ? value : next.month);
        const yearNum = parseInt(name === "year" ? value : next.year) || 2000;
        const maxDay = new Date(yearNum, monthIndex + 1, 0).getDate();
        if (parseInt(next.day) > maxDay) next.day = String(maxDay);
      }
      return next;
    });
  };

  const handleGoogleSignIn = () => {
    if (from?.startsWith("/evento/")) {
      sessionStorage.setItem("auth_return_to", from);
    }
    const redirectUri = `${window.location.origin}/auth/callback`;
    const cognitoDomain = import.meta.env.VITE_COGNITO_DOMAIN;
    const clientId = import.meta.env.VITE_COGNITO_CLIENT_ID;
    window.location.href = `${cognitoDomain}/oauth2/authorize?client_id=${clientId}&response_type=code&scope=openid+email+profile&redirect_uri=${encodeURIComponent(redirectUri)}&identity_provider=Google&prompt=select_account`;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const newErrors = validate();
    setErrors(newErrors);
    if (Object.keys(newErrors).length === 0) {
      const bornDate = `${form.year}-${MONTHS.indexOf(form.month) + 1}-${form.day}`;
      dispatch(registerUser({
        nombre: form.name,
        apellido: form.lastname,
        tipoDocumento: form.tipoDocumento,
        numeroTributario: form.tipoDocumento === "rut" ? form.rut : null,
        numeroPasaporte: form.tipoDocumento === "pasaporte" ? form.numeroPasaporte : null,
        correo: form.email,
        telefono: null,
        fechaNacimiento: bornDate,
        genero: form.gender,
        imagenDePerfil: null,
        enabled: 1,
        perEmpresa: 1,
        perEmbajador: 0,
        perValidador: 0,
        facebook: form.socialNetwork === "facebook" ? form.socialHandle || null : null,
        instagram: form.socialNetwork === "instagram" ? form.socialHandle || null : null,
        tiktok: form.socialNetwork === "tiktok" ? form.socialHandle || null : null,
        pais: "",
        ciudad: "",
        password: form.password,
        ...(from?.startsWith("/evento/") ? { returnTo: from } : {}),
      }));
    }
  };

  if (isAuthenticated) return <Navigate to="/" replace />;

  return (
    <div className="relative flex h-screen w-screen overflow-hidden">
      {/* Fondo desktop — grid de eventos con gradiente oscuro */}
      <div className="absolute inset-0 hidden md:block bg-aw-bluedark">
        <img src={BgImage} alt="" className="w-full h-full object-cover object-center" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/92 via-black/70 to-black/35" />
      </div>

      {/* Panel formulario — ancho fijo en desktop, pantalla completa en mobile */}
      <div className="relative z-10 flex-1 md:flex-none md:w-[500px] flex flex-col bg-aw-bluedark md:bg-aw-bluedark/80 md:backdrop-blur-sm text-white overflow-y-auto scrollbar-box">
        {/* Imagen de fondo solo en mobile */}
        <div className="absolute inset-0 md:hidden">
          <img src={SignInImage} alt="Sign In" className="w-full h-full object-cover object-center" />
          <div className="absolute inset-0 bg-aw-bluedark-75 backdrop-blur-[2px]" />
        </div>

        <div className="relative px-8 py-6 flex flex-col flex-1">
          <span onClick={() => navigate(-1)} className="absolute left-4 top-4 cursor-pointer">
            <FontAwesomeIcon
              icon="fa-solid fa-arrow-left"
              className="text-white hover:text-gray-400 duration-200 font-bold text-lg"
            />
          </span>

          {sendRegister == false ? (
            <form className="flex flex-col gap-2 shadow-2xl" onSubmit={handleSubmit}>
              {/* Logo + título */}
              <div className="flex justify-center mb-1 mt-6">
                <img src={LogoAppWhoop} alt="AppWhoop Logo" className="w-40 h-auto object-contain" />
              </div>
              <h3 className="text-xl font-bold text-center mb-3">
                Registrarme en AppWhoop Ticket
              </h3>

              {/* ── Sección de registro social (prominente) ── */}
              <div className="flex flex-col gap-2">
                {/* Google — activo */}
                <button
                  type="button"
                  onClick={handleGoogleSignIn}
                  className="w-full flex items-center justify-center gap-3 bg-white text-gray-800 font-semibold py-3 rounded-xl hover:bg-gray-50 hover:scale-95 transition-all duration-200 text-sm shadow-md"
                >
                  <FontAwesomeIcon icon="fa-brands fa-google" className="text-[#DB4437] text-base" />
                  Continuar con Google
                </button>
                {/* Proveedores futuros */}
                <div className="flex gap-2">
                  <button
                    type="button"
                    disabled
                    title="Próximamente"
                    className="flex-1 flex items-center justify-center gap-2 bg-white/8 text-white/35 font-medium py-2.5 rounded-xl text-sm cursor-not-allowed border border-white/10"
                  >
                    <FontAwesomeIcon icon="fa-brands fa-apple" className="text-base" />
                    Apple
                    <span className="text-[10px] bg-white/10 px-1.5 py-0.5 rounded-full leading-tight">Próx.</span>
                  </button>
                  <button
                    type="button"
                    disabled
                    title="Próximamente"
                    className="flex-1 flex items-center justify-center gap-2 bg-white/8 text-white/35 font-medium py-2.5 rounded-xl text-sm cursor-not-allowed border border-white/10"
                  >
                    <FontAwesomeIcon icon="fa-brands fa-facebook" className="text-base" />
                    Facebook
                    <span className="text-[10px] bg-white/10 px-1.5 py-0.5 rounded-full leading-tight">Próx.</span>
                  </button>
                </div>
              </div>

              {/* Divisor */}
              <div className="flex items-center gap-3 my-1">
                <div className="flex-1 h-px bg-white/15" />
                <span className="text-xs text-white/50">o regístrate con tu email</span>
                <div className="flex-1 h-px bg-white/15" />
              </div>

              {/* Nombre + Apellido */}
              <div className="flex gap-2">
                <div className="flex-1">
                  <input
                    type="text"
                    name="name"
                    placeholder="Nombre"
                    value={form.name}
                    onChange={handleChange}
                    className="w-full rounded-xl px-4 py-2.5 bg-white/90 text-black placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-aw-green"
                  />
                  {errors.name && <span className="text-xs text-red-500">{errors.name}</span>}
                </div>
                <div className="flex-1">
                  <input
                    type="text"
                    name="lastname"
                    placeholder="Apellidos"
                    value={form.lastname}
                    onChange={handleChange}
                    className="w-full rounded-xl px-4 py-2.5 bg-white/90 text-black placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-aw-green"
                  />
                  {errors.lastname && <span className="text-xs text-red-500">{errors.lastname}</span>}
                </div>
              </div>

              {/* Tipo de documento */}
              <div>
                <label className="text-sm font-medium text-white mb-2 block">Documento de identidad *</label>
                <div className="flex gap-2 mb-3">
                  <button
                    type="button"
                    onClick={() => setForm(prev => ({ ...prev, tipoDocumento: "rut", numeroPasaporte: "" }))}
                    className={`flex-1 py-2 rounded-xl text-sm font-medium transition-colors ${form.tipoDocumento === "rut" ? "bg-aw-green text-aw-bluedark" : "bg-white/10 text-white hover:bg-white/20"}`}
                  >
                    Soy Chileno (RUT)
                  </button>
                  <button
                    type="button"
                    onClick={() => setForm(prev => ({ ...prev, tipoDocumento: "pasaporte", rut: "" }))}
                    className={`flex-1 py-2 rounded-xl text-sm font-medium transition-colors ${form.tipoDocumento === "pasaporte" ? "bg-aw-green text-aw-bluedark" : "bg-white/10 text-white hover:bg-white/20"}`}
                  >
                    Soy Extranjero (Pasaporte)
                  </button>
                </div>

                {form.tipoDocumento === "rut" ? (
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs bg-amber-500/20 text-amber-300 border border-amber-500/30 px-2 py-0.5 rounded-full font-medium">
                        No editable después del registro
                      </span>
                    </div>
                    <input
                      type="text"
                      name="rut"
                      placeholder="ej: 12.345.678-9"
                      value={form.rut}
                      onChange={handleRutChange}
                      maxLength={12}
                      className={`w-full rounded-xl px-4 py-2.5 bg-white/90 text-black placeholder-gray-500 focus:outline-none focus:ring-2 ${rutError ? "ring-1 ring-red-400 focus:ring-red-500" : "focus:ring-aw-green"}`}
                    />
                    {(rutError || errors.rut) ? (
                      <span className="text-xs text-red-500">{rutError || errors.rut}</span>
                    ) : (
                      <p className="text-xs text-amber-300/80 flex items-start gap-1 mt-1">
                        <FontAwesomeIcon icon="fa-solid fa-triangle-exclamation" className="text-amber-400 mt-0.5 shrink-0" style={{ fontSize: "10px" }} />
                        Requerido para comprar tickets. Asegúrate que esté correcto, no podrás modificarlo luego.
                      </p>
                    )}
                  </div>
                ) : (
                  <div>
                    <input
                      type="text"
                      name="numeroPasaporte"
                      placeholder="ej: AB1234567"
                      value={form.numeroPasaporte}
                      onChange={handlePasaporteChange}
                      maxLength={20}
                      className={`w-full rounded-xl px-4 py-2.5 bg-white/90 text-black placeholder-gray-500 focus:outline-none focus:ring-2 ${errors.numeroPasaporte ? "ring-1 ring-red-400 focus:ring-red-500" : "focus:ring-aw-green"}`}
                    />
                    {errors.numeroPasaporte && (
                      <span className="text-xs text-red-500">{errors.numeroPasaporte}</span>
                    )}
                    <p className="text-xs text-blue-300/80 flex items-start gap-1 mt-1">
                      <FontAwesomeIcon icon="fa-solid fa-circle-info" className="text-blue-400 mt-0.5 shrink-0" style={{ fontSize: "10px" }} />
                      Ingresa el número que aparece en tu pasaporte (6-20 caracteres).
                    </p>
                  </div>
                )}
              </div>

              {/* Red social */}
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <label className="text-sm font-medium text-white">Red social</label>
                  <span className="text-[10px] bg-aw-green/15 text-aw-green border border-aw-green/25 px-2 py-0.5 rounded-full font-medium">
                    Opcional para comprar
                  </span>
                </div>
                <div className="flex gap-2">
                  <select
                    name="socialNetwork"
                    value={form.socialNetwork}
                    onChange={handleChange}
                    className="w-2/5 rounded-xl px-3 py-2.5 bg-white/90 text-black focus:outline-none focus:ring-2 focus:ring-aw-green"
                  >
                    {SOCIAL_NETWORKS.map((sn) => (
                      <option key={sn.value} value={sn.value}>{sn.label}</option>
                    ))}
                  </select>
                  <input
                    type="text"
                    name="socialHandle"
                    placeholder="@usuario"
                    value={form.socialHandle}
                    onChange={handleChange}
                    className="flex-1 rounded-xl px-4 py-2.5 bg-white/90 text-black placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-aw-green"
                  />
                </div>
                <p className="text-xs text-white/45 flex items-start gap-1 mt-1">
                  <FontAwesomeIcon icon="fa-solid fa-circle-info" className="text-aw-green/70 mt-0.5 shrink-0" style={{ fontSize: "10px" }} />
                  Requerida para postularte a eventos, pases gratuitos o con descuento, y tener mayores probabilidades de aceptación.
                </p>
              </div>

              {/* Género */}
              <div className="flex justify-center gap-2 my-1">
                {GENDERS.map((g) => (
                  <label
                    key={g.value}
                    className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium cursor-pointer transition-all duration-200 border ${
                      form.gender === g.value
                        ? "bg-aw-green text-black border-aw-green"
                        : "bg-white/10 text-white border-white/20 hover:bg-white/20"
                    }`}
                  >
                    <input
                      type="radio"
                      name="gender"
                      value={g.value}
                      checked={form.gender === g.value}
                      onChange={handleChange}
                      className="sr-only"
                    />
                    {g.label}
                  </label>
                ))}
              </div>

              {/* Fecha de nacimiento */}
              <label className="flex items-center gap-2 text-white text-sm">
                Fecha de Nacimiento *
                <Tooltip
                  content="Utilizaremos este dato para enviarte información, beneficios y promociones"
                  position="bottom"
                >
                  <FontAwesomeIcon icon="fa-regular fa-circle-question" className="text-gray-400 text-xs" />
                </Tooltip>
              </label>
              <div className="flex justify-between gap-2">
                <select
                  name="day"
                  value={form.day}
                  onChange={handleChange}
                  className="w-1/5 rounded-xl px-2 py-2.5 bg-white/90 text-black focus:outline-none focus:ring-2 focus:ring-aw-green"
                >
                  <option value="">Día</option>
                  {Array.from(
                    { length: new Date(parseInt(form.year) || 2000, MONTHS.indexOf(form.month) + 1, 0).getDate() },
                    (_, i) => i + 1
                  ).map((d) => (
                    <option key={d} value={String(d)}>{d}</option>
                  ))}
                </select>
                <select
                  name="month"
                  value={form.month}
                  onChange={handleChange}
                  className="w-3/5 rounded-xl px-3 py-2.5 bg-white/90 text-black focus:outline-none focus:ring-2 focus:ring-aw-green"
                >
                  {MONTHS.map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
                <input
                  type="number"
                  name="year"
                  min="1900"
                  max={new Date().getFullYear()}
                  placeholder="Año"
                  value={form.year}
                  onChange={handleChange}
                  className="w-1/5 rounded-xl px-3 py-2.5 bg-white/90 text-black placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-aw-green"
                />
              </div>
              {errors.fechaNacimiento && (
                <span className="text-xs text-red-500">{errors.fechaNacimiento}</span>
              )}

              {/* Email */}
              <input
                type="email"
                name="email"
                placeholder="Correo electrónico"
                value={form.email}
                onChange={handleChange}
                className="w-full rounded-xl px-4 py-2.5 bg-white/90 text-black placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-aw-green"
              />
              {errors.email && <span className="text-xs text-red-500">{errors.email}</span>}

              {/* Contraseña */}
              <label className="flex items-center gap-1.5 text-white text-sm font-medium">Contraseña</label>
              <div className="relative">
                <input
                  ref={passwordInputRef}
                  type={showPassword ? "text" : "password"}
                  name="password"
                  id="password"
                  value={form.password}
                  onChange={handleChange}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-xl bg-white text-black focus:outline-none focus:ring-2 focus:ring-aw-bluedark pr-10"
                  placeholder="Contraseña"
                />
                <button
                  type="button"
                  className="h-full w-1/12 absolute top-1 right-1 flex justify-center items-center text-gray-500 hover:text-aw-bluedark focus:outline-none duration-200 px-2"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                    const input = passwordInputRef.current;
                    const start = input.selectionStart;
                    const end = input.selectionEnd;
                    setShowPassword((prev) => !prev);
                    setTimeout(() => { input.focus(); input.setSelectionRange(start, end); }, 0);
                  }}
                  tabIndex={-1}
                >
                  <AnimatePresence>
                    <Motion.div
                      key={showPassword ? "eye-slash" : "eye"}
                      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                      transition={{ duration: 0.5 }}
                    >
                      <FontAwesomeIcon
                        icon={showPassword ? "fa-regular fa-eye-slash" : "fa-regular fa-eye"}
                        className="absolute top-2.5 right-3"
                      />
                    </Motion.div>
                  </AnimatePresence>
                </button>
              </div>
              {errors.password && <span className="text-xs text-red-500">{errors.password}</span>}
              {form.password && (
                <div className="flex flex-wrap gap-x-3 gap-y-1 bg-white/5 rounded-xl px-3 py-2">
                  {[
                    { ok: form.password.length >= 8, label: "8+ chars" },
                    { ok: /[A-Z]/.test(form.password), label: "Mayúscula" },
                    { ok: /[a-z]/.test(form.password), label: "Minúscula" },
                    { ok: /[0-9]/.test(form.password), label: "Número" },
                    { ok: /[!@#$%^&*(),.?":{}|<>]/.test(form.password), label: "Símbolo" },
                  ].map(({ ok, label }) => (
                    <span key={label} className={`text-xs flex items-center gap-1 transition-colors duration-200 ${ok ? "text-aw-green" : "text-gray-400"}`}>
                      <FontAwesomeIcon icon={ok ? "fa-solid fa-check" : "fa-solid fa-xmark"} className="text-xs" />
                      {label}
                    </span>
                  ))}
                </div>
              )}

              {/* Confirmar contraseña */}
              <div className="relative">
                <input
                  ref={confirmPasswordInputRef}
                  type={showConfirmPassword ? "text" : "password"}
                  name="repeatPassword"
                  id="repeatPassword"
                  value={form.repeatPassword}
                  onChange={handleChange}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-xl bg-white text-black focus:outline-none focus:ring-2 focus:ring-aw-bluedark pr-10"
                  placeholder="Confirmar Contraseña"
                />
                <button
                  type="button"
                  className="h-full w-1/12 absolute top-1 right-1 flex justify-center items-center text-gray-500 hover:text-aw-bluedark focus:outline-none duration-200 px-2"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                    const input = confirmPasswordInputRef.current;
                    const start = input.selectionStart;
                    const end = input.selectionEnd;
                    setShowConfirmPassword((prev) => !prev);
                    setTimeout(() => { input.focus(); input.setSelectionRange(start, end); }, 0);
                  }}
                  tabIndex={-1}
                >
                  <AnimatePresence>
                    <Motion.div
                      key={showConfirmPassword ? "eye-slash" : "eye"}
                      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                      transition={{ duration: 0.5 }}
                    >
                      <FontAwesomeIcon
                        icon={showConfirmPassword ? "fa-regular fa-eye-slash" : "fa-regular fa-eye"}
                        className="text-base absolute top-2.5 right-3"
                      />
                    </Motion.div>
                  </AnimatePresence>
                </button>
              </div>
              {errors.repeatPassword && <span className="text-xs text-red-500">{errors.repeatPassword}</span>}

              {/* Botón registrar */}
              <button
                type="submit"
                disabled={loading || (form.tipoDocumento === "rut" && !!rutError)}
                className="w-full bg-aw-green text-black font-semibold py-3 rounded-xl flex items-center justify-center gap-2 text-base shadow-lg transition-transform duration-300 hover:scale-95 disabled:opacity-70 disabled:cursor-not-allowed disabled:hover:scale-100"
              >
                <FontAwesomeIcon
                  icon={loading ? "fa-solid fa-circle-notch" : "fa-solid fa-user-circle"}
                  className={`text-xl${loading ? " animate-spin" : ""}`}
                />
                {loading ? "Enviando..." : "Registrarme"}
              </button>

              <hr className="mt-3 border-t border-white/10" />
              <div className="text-xs text-white text-center mt-1">
                Al hacer clic en "Registrarme", acepta nuestra<br />
                <a href="/politica-de-privacidad-y-terminos-y-condiciones" className="underline cursor-pointer">
                  Política de Privacidad y Terminos y condiciones.
                </a>
              </div>
              <div className="text-xs text-white text-center mt-1">
                Puede recibir nuestras notificaciones por correo y puede optar por no recibirlas en cualquier momento.
              </div>
              <div className="text-xs text-white text-center mt-2 mb-1">
                © {new Date().getFullYear()} AppWhoop Ticket. Todos los derechos reservados
              </div>
            </form>
          ) : error ? (
            <div className="text-red-500 text-center mt-2">
              <FontAwesomeIcon
                icon="fa-solid fa-circle-exclamation"
                className="text-4xl mx-auto mb-2 animate-bounce"
              />
              <h4 className="text-2xl text-gray-300 font-semibold">{error.message}</h4>
              <button
                onClick={() => dispatch(registerReset())}
                className="mt-4 px-4 py-2 bg-aw-green text-aw-dark rounded-lg font-semibold hover:bg-aw-green/90 hover:scale-95 transition-transform duration-300"
              >
                Intentar de nuevo
              </button>
            </div>
          ) : (
            <div className="text-aw-green text-center mt-2">
              <FontAwesomeIcon
                icon="fa-solid fa-circle-check"
                className="text-4xl mx-auto mb-2 animate-bounce"
              />
              <h4 className="text-4xl text-gray-300 font-semibold">Registro Exitoso!</h4>
              <p className="mt-2">
                Por favor, revisa tu correo electrónico para confirmar tu cuenta ANTES DE INICIAR SESIÓN POR PRIMERA VEZ.
              </p>
              <button
                onClick={() => {
                  dispatch(registerReset());
                  navigate("/login", { state: from?.startsWith("/evento/") ? { from } : undefined });
                }}
                className="mt-4 px-4 py-2 bg-aw-green text-aw-dark rounded-lg font-semibold hover:bg-aw-green/90 hover:scale-95 transition-transform duration-300"
              >
                Volver a Iniciar Sesión
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Panel branding — solo desktop */}
      <div className="hidden md:flex flex-1 relative z-10 flex-row items-center justify-center px-12 gap-10">
        <div className="flex-1 min-w-0">
          <p className="text-aw-green font-semibold text-sm uppercase tracking-widest mb-4">
            AppWhoop Ticket
          </p>
          <h1 className="text-5xl font-extrabold text-white leading-tight drop-shadow-lg">
            Únete a la<br />comunidad de eventos
          </h1>
          <p className="text-white/55 text-lg mt-4 leading-relaxed">
            Regístrate gratis y accede a tickets,<br />
            pases exclusivos y mucho más.
          </p>
        </div>
        <div className="relative w-6/12 shrink-0 -rotate-1">
          <div className="absolute -inset-4 bg-aw-green/10 rounded-3xl blur-2xl" />
          <img
            src={AppLayout}
            alt="AppWhoop App"
            className="relative w-full h-auto rounded-2xl"
          />
        </div>
      </div>
    </div>
  );
}
